/**
 * Phase F customer-facing project sections.
 * Used inside customer OrderDetail.jsx to surface:
 *  - 7-stage workflow progress
 *  - Pending approvals (Approve / Request Revision)
 *  - Project files (latest of each)
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { CheckCircle, MessageSquare, Folder, Download, Loader2, X, FileText } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = {
  requirement_collection: "Requirements",
  design: "Designing",
  internal_review: "Internal QC",
  client_review: "Client Review",
  revisions: "Revisions",
  approval: "Approval",
  delivery: "Delivery",
  completed: "Completed",
};
const STAGES = Object.keys(STAGE_LABELS);

export function ProjectStagesCard({ projectId }) {
  const [project, setProject] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}`).then(r => setProject(r.data)).catch(() => {});
  }, [projectId]);
  if (!project) return null;
  const idx = STAGES.indexOf(project.workflow_stage);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="cust-project-stages">
      <h3 className="text-lg font-bold text-[#1a3c5e]">Project Workflow</h3>
      <div className="mt-1 text-xs text-gray-500">Stage {idx + 1} of {STAGES.length}: <strong>{STAGE_LABELS[project.workflow_stage]}</strong></div>
      <div className="mt-3 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-[#F7941D] to-[#e0861a]" style={{ width: `${project.workflow_progress || 0}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-4 sm:grid-cols-8 gap-1 text-[10px]">
        {STAGES.map((s, i) => (
          <div key={s} className={`px-1.5 py-1.5 rounded text-center ${i <= idx ? "bg-[#FEF3E2] text-[#1a3c5e] font-bold" : "bg-gray-50 text-gray-400"}`}>
            <div>{i + 1}</div>
            <div className="truncate">{STAGE_LABELS[s]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function RequirementsSummaryCard({ projectId }) {
  const [req, setReq] = useState(null);
  const [schema, setSchema] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/requirement`).then(r => {
      setReq(r.data.requirement);
      setSchema(r.data.schema);
    }).catch(() => {});
  }, [projectId]);

  if (!req || !schema || Object.keys(req.data || {}).length === 0) return null;

  const rows = [];
  for (const sec of schema.sections) {
    for (const f of sec.fields) {
      const v = req.data[f.key];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      let display;
      if (f.type === "boolean") display = v ? "Yes" : "No";
      else if (f.type === "files") display = `${v.length} photo${v.length === 1 ? "" : "s"}`;
      else display = String(v);
      rows.push({ key: f.key, label: f.label, display });
    }
  }
  if (rows.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="cust-requirements-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-[#1a3c5e]">Your Requirements</h3>
        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border font-bold ${req.status === "submitted" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-amber-100 text-amber-700 border-amber-300"}`}>{req.status === "submitted" ? "Submitted" : "Draft"}</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 text-sm">
        {rows.map(r => (
          <div key={r.key}>
            <div className="text-xs text-gray-500">{r.label}</div>
            <div className="font-semibold text-[#1a3c5e]">{r.display}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


export function ApprovalsCustomerCard({ projectId, onChange }) {
  const [list, setList] = useState([]);
  const [decideOn, setDecideOn] = useState(null);
  const [decision, setDecision] = useState("approved");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/projects/${projectId}/approvals`).then(r => setList(r.data));
  useEffect(() => { load(); }, [projectId]);

  const submit = async () => {
    if (decision === "revision" && !notes.trim()) { toast.error("Revision notes required"); return; }
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/approvals/${decideOn.id}/decide`, { decision, notes });
      toast.success(decision === "approved" ? "Approved" : "Revision requested");
      setDecideOn(null); setNotes(""); setDecision("approved");
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  const pending = list.filter(a => a.status === "pending");
  if (list.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="cust-approvals-card">
      <h3 className="text-lg font-bold text-[#1a3c5e]">Drawing Approvals</h3>
      {pending.length > 0 && (
        <div className="mt-2 mb-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2 text-sm font-semibold">
          {pending.length} drawing(s) waiting for your review.
        </div>
      )}
      <div className="space-y-2 mt-2">
        {list.map(a => (
          <div key={a.id} className="border border-gray-200 rounded-xl p-3" data-testid={`cust-approval-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-[#1a3c5e]">{a.title}</div>
                {a.description && <div className="text-sm text-gray-600 mt-0.5">{a.description}</div>}
                {a.file && (
                  <a href={`${api.defaults.baseURL}/project-files/${a.file_id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[#F7941D] hover:underline mt-1 inline-block">
                    View File: {a.file.file_name}
                  </a>
                )}
              </div>
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${
                a.status === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                : a.status === "revision_requested" ? "bg-rose-100 text-rose-700 border-rose-300"
                : "bg-amber-100 text-amber-700 border-amber-300"}`}>{a.status.replace("_", " ")}</span>
            </div>
            {a.decision_notes && <div className="mt-2 bg-gray-50 px-2 py-1.5 rounded text-xs text-gray-700"><strong>Your notes:</strong> {a.decision_notes}</div>}
            {a.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`cust-approve-${a.id}`} onClick={() => { setDecideOn(a); setDecision("approved"); }} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Approve</button>
                <button data-testid={`cust-revise-${a.id}`} onClick={() => { setDecideOn(a); setDecision("revision"); }} className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Request Revision</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {decideOn && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cust-decision-modal">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="font-bold text-[#1a3c5e]">{decision === "approved" ? "Approve" : "Request Revision"}</h3>
              <button onClick={() => setDecideOn(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm">For: <strong>{decideOn.title}</strong></div>
              <textarea data-testid="cust-decision-notes" rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder={decision === "revision" ? "Tell the designer what to change (required)…" : "Optional notes…"} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div className="border-t border-gray-200 p-3 flex justify-end gap-2">
              <button onClick={() => setDecideOn(null)} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
              <button onClick={submit} disabled={submitting} data-testid="cust-decision-submit"
                className={`px-5 py-2 text-white text-sm font-bold rounded-xl inline-flex items-center gap-1 ${decision === "approved" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-rose-500 hover:bg-rose-600"} disabled:opacity-50`}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {decision === "approved" ? "Approve" : "Request Revision"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function ProjectFilesCustomerCard({ projectId }) {
  const [data, setData] = useState({ files: [], folders: [] });
  useEffect(() => { api.get(`/projects/${projectId}/files`).then(r => setData(r.data)); }, [projectId]);
  if (!data.files.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="cust-files-card">
      <h3 className="text-lg font-bold text-[#1a3c5e] flex items-center gap-2"><Folder className="w-5 h-5 text-[#F7941D]" /> Project Files</h3>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-3">
        {data.files.map(f => (
          <a key={f.id} href={`${api.defaults.baseURL}/project-files/${f.id}/download`} target="_blank" rel="noreferrer"
            className="block border border-gray-200 rounded-xl p-3 hover:border-[#F7941D] hover:shadow-sm" data-testid={`cust-file-${f.id}`}>
            <div className="text-sm font-semibold truncate">{f.file_name}</div>
            <div className="text-[10px] uppercase text-gray-500 mt-0.5">{f.folder} · v{f.version}</div>
            <div className="mt-1 text-xs text-[#F7941D] inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download</div>
          </a>
        ))}
      </div>
    </div>
  );
}


export function InvoiceCard({ projectId }) {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get(`/customer/invoices?order_id=${projectId}`).then(r => setInvoices(r.data)).catch(() => {}); }, [projectId]);
  if (invoices.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="cust-invoice-card">
      <h3 className="text-lg font-bold text-[#1a3c5e] flex items-center gap-2"><FileText className="w-5 h-5 text-[#F7941D]" /> Invoice</h3>
      <div className="space-y-2 mt-3">
        {invoices.map(inv => (
          <div key={inv.id} className="flex items-center justify-between border border-gray-200 rounded-xl p-3" data-testid={`cust-invoice-${inv.id}`}>
            <div>
              <div className="text-sm font-semibold text-[#1a3c5e]">{inv.invoice_no}</div>
              <div className="text-xs text-gray-500">
                {inv.status === "paid" ? "Paid in full" : `Due ${(inv.due_date || "").slice(0, 10)}`} · ₹{(inv.grand_total || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <a href={`${api.defaults.baseURL}/sales/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`cust-invoice-download-${inv.id}`}
              className="px-3 py-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 flex-shrink-0">
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
